"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { ArrowsLeftRight, ListChecks, Users, ShieldCheck, type IconProps } from "@phosphor-icons/react";

/** 四个特性卡片：图标在左上，浅绿方框+绿边（与设计图一致） */
const FEATURE_KEYS = [
	{ icon: ArrowsLeftRight, titleKey: "feature1Title", descKey: "feature1Desc" },
	{ icon: ListChecks, titleKey: "feature2Title", descKey: "feature2Desc" },
	{ icon: Users, titleKey: "feature3Title", descKey: "feature3Desc" },
	{ icon: ShieldCheck, titleKey: "feature4Title", descKey: "feature4Desc" },
] as const;

const iconSize = 22;
const iconProps: IconProps = { size: iconSize, weight: "fill", className: "text-themePrimary400" };

export interface InjectionMoldingWhyChooseProps {
	translationNamespace?: string;
	quoteHref?: string;
	ctaImageLeft?: string;
	ctaImageRight?: string;
}

export default function InjectionMoldingWhyChoose({
	translationNamespace = "injectionMoldingWhyChoose",
	quoteHref = "/upload",
	ctaImageLeft,
	ctaImageRight,
}: InjectionMoldingWhyChooseProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white">
			<div className="container px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:py-16">
				{/* 标题：两行，第二行右缩进；右侧绿色按钮 */}
				<div className="mb-6 flex flex-col gap-6 sm:mb-8 sm:flex-row sm:items-center sm:justify-between md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("titleLine1")}</span>
						<span className="block">{t("titleLine2")}</span>
					</h2>
					<a
						href={quoteHref}
						target="_blank"
						rel="noopener noreferrer"
						className="shrink-0 rounded-xl bg-themePrimary400 px-6 py-3.5 text-sm font-bold text-white sm:px-8 sm:py-4 sm:text-base"
					>
						{t("topButton")}
					</a>
				</div>

				{/* 布局：第一行 3 张卡片，第二行 1 张卡片 + CTA（占两列宽） */}
				<div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-3 md:gap-6">
					{/* 前 3 张卡片 */}
					{FEATURE_KEYS.slice(0, 3).map((item) => {
						const Icon = item.icon;
						return (
							<div
								key={item.titleKey}
								className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 text-left shadow-[0_1px_8px_rgba(0,0,0,0.06)] sm:p-6"
							>
								<div className="mb-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-themePrimary400 bg-[#f0f9eb]">
									<Icon {...iconProps} />
								</div>
								<h3 className="mb-2 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
							</div>
						);
					})}
					{/* 第 4 张卡片：左下 */}
					<div className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 text-left shadow-[0_1px_8px_rgba(0,0,0,0.06)] sm:p-6 md:row-start-2">
						{(() => {
							const item = FEATURE_KEYS[3];
							const Icon = item.icon;
							return (
								<>
									<div className="mb-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-themePrimary400 bg-[#f0f9eb]">
										<Icon {...iconProps} />
									</div>
									<h3 className="mb-2 text-lg font-bold text-black sm:text-xl md:text-2xl">
										{t(item.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
								</>
							);
						})()}
					</div>
					{/* 底部 CTA：右下，占两列；背景图左 2 份、右 1 份并排 */}
					<div
						className="relative min-h-[280px] overflow-hidden rounded-lg bg-[#4a4a4a] px-5 py-10 sm:min-h-[300px] sm:px-6 sm:py-12 md:col-span-2 md:row-start-2"
						style={{ backgroundColor: "#4a4a4a" }}
					>
						{(ctaImageLeft || ctaImageRight) && (
							<div className="pointer-events-none absolute inset-0 flex">
								{/* 左图占 2/3 宽 */}
								{ctaImageLeft && (
									<div className="relative h-full w-2/3 shrink-0 opacity-55">
										<Image
											src={ctaImageLeft}
											alt=""
											fill
											className="object-cover object-center"
											sizes="66vw"
										/>
									</div>
								)}
								{/* 右图占 1/3 宽 */}
								{ctaImageRight && (
									<div className="relative h-full w-1/3 shrink-0 opacity-55">
										<Image
											src={ctaImageRight}
											alt=""
											fill
											className="object-cover object-center"
											sizes="33vw"
										/>
									</div>
								)}
							</div>
						)}
						<div className="relative flex h-full min-h-[240px] flex-col items-center justify-center text-center">
							<h3 className="mb-2 text-xl font-bold text-white sm:text-2xl md:text-3xl">{t("ctaTitle")}</h3>
							<p className="mb-6 text-sm text-white/90">{t("ctaSubtitle")}</p>
							<a
								href={quoteHref}
								target="_blank"
								rel="noopener noreferrer"
								className="rounded-xl bg-themePrimary400 px-8 py-3.5 text-base font-bold text-white hover:opacity-95"
							>
								{t("ctaButton")}
							</a>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
